import merge from 'lodash/merge';

import type { GetCareerFeedQuery } from 'src/graphql/graphql';

export type CareerFeed = NonNullable<
  NonNullable<GetCareerFeedQuery['careerFeed']>
>;

type CareerFeedItem = NonNullable<
  NonNullable<CareerFeed['edges']>[number]
>['node'];

type CareerFeedJob = Extract<CareerFeedItem, { __typename: 'CareerFeedJob' }>;

const DEFAULT_JOB_ID = '29663948';

export const mockFeedJob = (
  careerFeedJob: Partial<CareerFeedJob> = {},
): CareerFeedJob =>
  merge(
    {
      __typename: 'CareerFeedJob',
      id: careerFeedJob.job?.id || DEFAULT_JOB_ID,
      isSaved: false,
      badges: [
        {
          label: 'New to you',
          type: 'NEW',
        },
        {
          label: 'Viewed',
          type: 'VIEWED',
        },
        {
          label: 'Expiring soon',
          type: 'EXPIRES_SOON',
        },
        {
          label: 'Be an early applicant',
          type: 'EARLY_APPLICANT',
        },
        {
          label: 'Strong applicant',
          type: 'TOP_APPLICANT',
        },
      ],
      job: {
        __typename: 'Job',
        advertiser: {
          __typename: 'Advertiser',
          name: 'Surge Direct Pty Ltd',
        },
        id: DEFAULT_JOB_ID,
        title: 'Pastry Chef & Chef de Partie',
        location: {
          label: 'Cairns, Cairns & Far North QLD',
        },
        salary: null,
        listedAt: {
          label: '1h ago',
        },
        products: {
          branding: {
            logo: {
              url: 'https://image-service-cdn.seek.com.au/7681d821cee6efb70961d6436430839eac077c1e/f3c5292cec0e05e4272d9bf9146f390d366481d0',
            },
          },
        },
      },
      solMetadata: {
        token: '1~201b49e4-d75c-4e74-a267-cdf652e36e77',
        jobId: careerFeedJob.job?.id || DEFAULT_JOB_ID,
        tags: {
          'recs:recSysChannel': '',
          'recs:product': 'recommended_jobs_page',
          'recs:experimentGroup': '51',
          'recs:flight': '51',
        },
      },
    },
    careerFeedJob,
  );

export const getMockFeedItems = (): CareerFeedItem[] => [
  mockFeedJob(),
  mockFeedJob({
    isSaved: true,
    job: {
      advertiser: {
        __typename: 'Advertiser',
        name: 'OnCue Talent',
      },
      id: '29752529',
      title: "EXTRA's Wanted for TV Commercials, TV Series & Film in Melbourne",
      salary: {
        label: '$25.66 p/hr + 11.5% Super + NFP Salary Packaging',
      },
      location: {
        label: 'Oakleigh South, Victoria',
      },
      listedAt: {
        label: '2d ago',
      },
      products: {
        branding: {
          logo: {
            url: 'https://image-service-cdn.seek.com.au/2e92eb70e5c5adfdc1e0ac590b3c64debd9dc3a4/f3c5292cec0e05e4272d9bf9146f390d366481d0',
          },
        },
      },
    },
  }),
  mockFeedJob({
    isSaved: true,
    job: {
      advertiser: {
        __typename: 'Advertiser',
        name: 'Spirit',
      },
      id: '29677777',
      title:
        'No Experience Needed - Apply today start tomorrow - Full Time Appointment Setter',
      salary: {
        label: '$90,000 – $110,000 per year',
      },
      location: {
        label: 'North Sydney, Sydney NSW',
      },
      listedAt: {
        label: '9d ago',
      },
      products: {
        branding: {
          logo: {
            url: 'https://image-service-cdn.seek.com.au/f318d46b3b07d6d90a8f4120c48defbc7379a208/f3c5292cec0e05e4272d9bf9146f390d366481d0',
          },
        },
      },
    },
  }),
  mockFeedJob({
    job: {
      advertiser: {
        __typename: 'Advertiser',
        name: 'Charterhouse Professional Support',
      },
      id: '29752529',
      title: 'Senior EA/Business Manager $110,000 package',
      salary: {
        label: '$110,000 package',
        currencyLabel: 'MYR',
      },
      location: {
        label: 'Sydney',
      },
      listedAt: {
        label: '1d ago',
      },
      products: {
        branding: {
          logo: {
            url: 'https://image-service-cdn.seek.com.au/3585866db3300b0803a44f1491f50ac457b6f116/dffceafa8da80ae865f1fa835a87a74a26be6546',
          },
        },
      },
    },
  }),
  mockFeedJob({
    job: {
      advertiser: {
        __typename: 'Advertiser',
        name: 'Jobstreet',
      },
      id: '999',
      title: 'Error State for Job Detail',
      salary: {
        label: '$80,000 – $1,000,000 per year',
        currencyLabel: 'MYR',
      },
      location: {
        label: 'Kuala Lumpur, Malaysia',
      },
      listedAt: {
        label: '30d ago',
      },
      products: null,
    },
  }),
  ...Array(94)
    .fill(null)
    .map((_, index) =>
      mockFeedJob({
        job: {
          id: `111${index}`,
          title: `Not Boring Job ${index}`,
          advertiser: {
            __typename: 'Advertiser',
            name: `Cool company ${index}`,
          },
          location: {
            label: `Cool location ${index}`,
          },
        },
      }),
    ),
];

export const mockCareerFeedQueryResponse = (limit?: number | null) => {
  const feedItems = getMockFeedItems().slice(0, limit ?? 100);
  return {
    __typename: 'CareerFeed' as const,
    pageInfo: {
      __typename: 'CareerFeedPageInfo' as const,
      cursor: 'cursor',
      count: feedItems.length,
    },
    solMetadata: {},
    edges: [
      ...feedItems.map((feedItem) => ({
        node: feedItem,
        __typename: 'CareerFeedEdge' as const,
      })),
    ],
  };
};
